import { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import type { TipoProducto } from '@/types/producto';

interface CanvasBoardProps {
  columnas: number;
  filas: number;
  anchoCm: number;
  altoCm: number;
  tipo: TipoProducto;
  unidad?: string;
}

export interface CanvasBoardHandle {
  toDataURL: () => string;
}

export const CanvasBoard = forwardRef<CanvasBoardHandle, CanvasBoardProps>(
  function CanvasBoard({ columnas, filas, anchoCm, altoCm, tipo, unidad = 'gabinetes' }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useImperativeHandle(ref, () => ({
    toDataURL: () => canvasRef.current?.toDataURL('image/png') ?? '',
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const containerWidth = canvas.parentElement?.clientWidth || 600;
    const padding = 60;
    const availableWidth = containerWidth - padding * 2;
    const availableHeight = 300;

    const totalWidthCm = columnas * anchoCm;
    const totalHeightCm = filas * altoCm;

    const scaleX = availableWidth / totalWidthCm;
    const scaleY = availableHeight / totalHeightCm;
    const scale = Math.min(scaleX, scaleY);

    const boardWidth = totalWidthCm * scale;
    const boardHeight = totalHeightCm * scale;

    canvas.width = containerWidth;
    canvas.height = boardHeight + padding * 2;

    const offsetX = (containerWidth - boardWidth) / 2;
    const offsetY = padding;

    // Background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    const cellWidth = boardWidth / columnas;
    const cellHeight = boardHeight / filas;
    const fillColor = tipo === 'INDOOR' ? '#dbeafe' : '#dcfce7';
    const borderColor = tipo === 'INDOOR' ? '#3b82f6' : '#22c55e';

    for (let row = 0; row < filas; row++) {
      for (let col = 0; col < columnas; col++) {
        const x = offsetX + col * cellWidth;
        const y = offsetY + row * cellHeight;

        ctx.fillStyle = fillColor;
        ctx.fillRect(x + 1, y + 1, cellWidth - 2, cellHeight - 2);

        ctx.strokeStyle = borderColor;
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, cellWidth, cellHeight);
      }
    }

    // Border around full board
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 2;
    ctx.strokeRect(offsetX, offsetY, boardWidth, boardHeight);

    // Labels
    ctx.fillStyle = '#374151';
    ctx.font = '12px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';

    // Width label (top)
    const widthM = (totalWidthCm / 100).toFixed(2);
    ctx.fillText(`${widthM} m`, offsetX + boardWidth / 2, offsetY - 10);

    // Height label (left)
    ctx.save();
    const heightM = (totalHeightCm / 100).toFixed(2);
    ctx.translate(offsetX - 15, offsetY + boardHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(`${heightM} m`, 0, 0);
    ctx.restore();

    // Area label (center)
    const areaM2 = ((totalWidthCm * totalHeightCm) / 10000).toFixed(2);
    ctx.font = 'bold 14px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#1f2937';
    ctx.fillText(`${areaM2} m²`, offsetX + boardWidth / 2, offsetY + boardHeight / 2 + 5);

    // Grid info
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#6b7280';
    ctx.fillText(`${columnas} x ${filas} = ${columnas * filas} ${unidad}`, offsetX + boardWidth / 2, offsetY + boardHeight + 20);
  }, [columnas, filas, anchoCm, altoCm, tipo]);

  return (
    <div className="w-full">
      <canvas ref={canvasRef} className="w-full rounded-lg border border-gray-200" />
    </div>
  );
});
